import { DatabaseOutlined, TableOutlined } from '@ant-design/icons';
import { Alert, Descriptions, Tag } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import DataPreviewTable from './components/DataPreviewTable';
import ToolWrapper from './components/ToolWrapper';
import arrayToTableData from './utils/tableHelpers';

/**
 * Renders the QueryDatabase tool response.
 * Shows database query results and status.
 */
function QueryDatabaseTool({ item }) {
  const query = item.args?.natural_language_query || '';
  const resultKey = item.args?.result_key || '';
  const status = item.response?.status || '';
  const message = item.response?.message || '';
  const errors = item.response?.errors || [];
  const isPending = item.response === null || item.response === undefined;

  return (
    <ToolWrapper toolName="Database Query" isPending={isPending} icon={<DatabaseOutlined />}>
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="Query">{query}</Descriptions.Item>
        {resultKey && <Descriptions.Item label="Result Key">{resultKey}</Descriptions.Item>}
        {!isPending && (
          <>
            <Descriptions.Item label="Status">
              <Tag color={status === 'success' ? 'green' : 'red'}>{status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Message">{message}</Descriptions.Item>
          </>
        )}
      </Descriptions>
      {errors.length > 0 && (
        <Alert
          message="Errors"
          description={errors.join(', ')}
          type="error"
          style={{ marginTop: 16 }}
        />
      )}
    </ToolWrapper>
  );
}

QueryDatabaseTool.propTypes = {
  item: PropTypes.shape({
    args: PropTypes.shape({
      natural_language_query: PropTypes.string,
      result_key: PropTypes.string,
    }),
    response: PropTypes.shape({
      status: PropTypes.string,
      message: PropTypes.string,
      errors: PropTypes.arrayOf(PropTypes.string),
    }),
  }).isRequired,
};

/**
 * Renders the DisplayCachedData tool response.
 * Shows cached dataset preview with expandable full data view.
 */
function DisplayCachedDataTool({ item }) {
  const columns = item.args?.columns || [];
  const isPending = item.response === null || item.response === undefined;

  // Try multiple sources for data
  const fullRows =
    item.response?.data?.rows || item.response?.sample_rows || item.response?.rows || [];

  const { dataSource, columns: tableColumns } = arrayToTableData(fullRows, columns);

  const renderContent = () => {
    if (isPending) {
      return <div style={{ fontSize: 13, color: '#8c8c8c' }}>Loading cached data...</div>;
    }
    if (dataSource.length === 0) {
      return <Alert message="No data available" type="info" />;
    }
    return <DataPreviewTable dataSource={dataSource} columns={tableColumns} />;
  };

  return <div style={{ marginBottom: 16 }}>{renderContent()}</div>;
}

DisplayCachedDataTool.propTypes = {
  item: PropTypes.shape({
    args: PropTypes.shape({
      result_key: PropTypes.string,
      columns: PropTypes.arrayOf(PropTypes.string),
    }),
    response: PropTypes.shape({
      data: PropTypes.shape({
        rows: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.array])),
      }),
      sample_rows: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.array])),
      rows: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.array])),
    }),
  }).isRequired,
};

/**
 * Renders the GetRandomData tool response.
 * Shows random sample preview from a dataset.
 */
function GetRandomDataTool({ item }) {
  const resultKey = item.args?.result_key || '';
  const sampleSize = item.args?.sample_size || 0;
  const columns = item.args?.columns || [];
  const preview = item.response?.preview || item.response?.sample_rows || [];
  const isPending = item.response === null || item.response === undefined;

  const { dataSource, columns: tableColumns } = arrayToTableData(preview, columns);

  return (
    <ToolWrapper
      toolName={`Random Sample: ${resultKey}`}
      isPending={isPending}
      icon={<TableOutlined />}
    >
      {dataSource.length === 0 ? (
        <Alert message="No preview data available" type="info" />
      ) : (
        <>
          <div style={{ marginBottom: 8, fontSize: 13, color: '#595959' }}>
            Random sample preview (top 5 rows) — sample size: {sampleSize}
          </div>
          <DataPreviewTable dataSource={dataSource} columns={tableColumns} />
        </>
      )}
    </ToolWrapper>
  );
}

GetRandomDataTool.propTypes = {
  item: PropTypes.shape({
    args: PropTypes.shape({
      result_key: PropTypes.string,
      sample_size: PropTypes.number,
      columns: PropTypes.arrayOf(PropTypes.string),
    }),
    response: PropTypes.shape({
      preview: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.array])),
      sample_rows: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.array])),
    }),
  }).isRequired,
};

/**
 * Renders the GetRowRange tool response.
 * Shows a specific range of rows from a dataset.
 */
function GetRowRangeTool({ item }) {
  const resultKey = item.args?.result_key || '';
  const startRow = item.args?.start_row || 0;
  const endRow = item.args?.end_row || 0;
  const columns = item.response?.columns || [];
  const data = item.response?.data || [];
  const returnedRows = item.response?.returned_rows || 0;
  const totalRows = item.response?.total_rows || 0;
  const isPending = item.response === null || item.response === undefined;

  const { dataSource, columns: tableColumns } = arrayToTableData(data, columns);

  return (
    <ToolWrapper
      toolName={`Row Range: ${resultKey}`}
      isPending={isPending}
      icon={<TableOutlined />}
    >
      {dataSource.length === 0 ? (
        <Alert message="No data in requested range" type="info" />
      ) : (
        <>
          <div style={{ marginBottom: 8, fontSize: 13, color: '#595959' }}>
            Rows {startRow}–{endRow} preview (top 5 rows) — returned {returnedRows} / total{' '}
            {totalRows}
          </div>
          <DataPreviewTable dataSource={dataSource} columns={tableColumns} />
        </>
      )}
    </ToolWrapper>
  );
}

GetRowRangeTool.propTypes = {
  item: PropTypes.shape({
    args: PropTypes.shape({
      result_key: PropTypes.string,
      start_row: PropTypes.number,
      end_row: PropTypes.number,
    }),
    response: PropTypes.shape({
      columns: PropTypes.arrayOf(PropTypes.string),
      data: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.array])),
      returned_rows: PropTypes.number,
      total_rows: PropTypes.number,
    }),
  }).isRequired,
};

/**
 * Renders the GetColumnSummary tool response.
 * Shows statistical summary for a column.
 */
function GetColumnSummaryTool({ item }) {
  const resultKey = item.args?.result_key || '';
  const columnName = item.args?.column_name || '';
  const status = item.response?.status || '';
  const response = item.response || {};
  const isPending = item.response === null || item.response === undefined;

  return (
    <ToolWrapper toolName="Column Summary" isPending={isPending} icon={<TableOutlined />}>
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="Result Key">{resultKey}</Descriptions.Item>
        <Descriptions.Item label="Column">{columnName}</Descriptions.Item>
        {!isPending && (
          <>
            <Descriptions.Item label="Status">
              <Tag color={status === 'success' ? 'green' : 'red'}>{status}</Tag>
            </Descriptions.Item>
            {Object.entries(response).map(
              ([key, value]) =>
                key !== 'status' && (
                  <Descriptions.Item key={key} label={key}>
                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                  </Descriptions.Item>
                ),
            )}
          </>
        )}
      </Descriptions>
    </ToolWrapper>
  );
}

GetColumnSummaryTool.propTypes = {
  item: PropTypes.shape({
    args: PropTypes.shape({
      result_key: PropTypes.string,
      column_name: PropTypes.string,
    }),
    response: PropTypes.shape({
      status: PropTypes.string,
    }),
  }).isRequired,
};

/**
 * Renders the ListCachedResultsKeys tool response.
 * Shows all available cached data sources.
 */
function ListCachedResultsKeysTool({ item }) {
  const status = item.response?.status || '';
  const dataSources = item.response?.data_sources || [];
  const isPending = item.response === null || item.response === undefined;

  const { dataSource, columns } = arrayToTableData(dataSources);

  return (
    <ToolWrapper toolName="Cached Results" isPending={isPending} icon={<DatabaseOutlined />}>
      {!isPending && (
        <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Status">
            <Tag color={status === 'success' ? 'green' : 'red'}>{status}</Tag>
          </Descriptions.Item>
        </Descriptions>
      )}
      {dataSources.length === 0 ? (
        <Alert message="No cached results" type="info" />
      ) : (
        <DataPreviewTable dataSource={dataSource} columns={columns} previewRows={10} />
      )}
    </ToolWrapper>
  );
}

ListCachedResultsKeysTool.propTypes = {
  item: PropTypes.shape({
    response: PropTypes.shape({
      status: PropTypes.string,
      data_sources: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.array])),
    }),
  }).isRequired,
};

export {
  DisplayCachedDataTool,
  GetColumnSummaryTool,
  GetRandomDataTool,
  GetRowRangeTool,
  ListCachedResultsKeysTool,
  QueryDatabaseTool,
};
